/**
 * HomePage — IMPERIA AI (AI-first growth platform voor NL MKB)
 *
 * Positionering: PRODUCT + MANAGED SERVICE, geen klassiek agency.
 * Drie tiers consistent: Gratis Scan · Connected €14,95 · Managed €995,95→€2.495,95
 *
 * SEO: één H1, semantische H2/H3, JSON-LD Organization + Product + FAQPage + WebSite,
 * Open Graph via SEOHead, internal-links naar /diensten, /prijzen, /werkwijze, /over-ons, /contact.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import {
  ArrowRight, CheckCircle2, Sparkles, Search, Zap, Brain,
  TrendingUp, Target, MapPin, Globe, BarChart3, Link2, FileText,
  Crown, ChevronRight, Activity, Lock, Plug, UserCheck, Headphones,
  Bot, Wrench, ShieldCheck, Award, Star,
} from 'lucide-react';

/* ─── Hard-coded tiers — single source of truth, identiek in /prijzen ─── */
const TIERS = [
  {
    id: 'free',
    label: 'Gratis Scan',
    price: '€0',
    period: '60 seconden',
    tagline: 'Direct inzicht zonder verplichtingen',
    bullets: [
      'Volledige online groeiscan in < 60 sec',
      'Top-10 verbeterpunten met prioritering',
      'Concurrentenanalyse + lokale ranking',
      '3 backlink-kansen voor uw branche',
    ],
    cta: { label: 'Start gratis scan', href: '/register' },
    accent: '#64748b',
  },
  {
    id: 'self',
    label: 'Connected Self-Service',
    price: '€14,95',
    period: 'p/m excl. btw',
    tagline: 'Live dashboard + AI-suggesties — u voert zelf uit',
    bullets: [
      'Real-time dashboard met live data',
      'AI-suggesties per opdracht (Huidig → Beter)',
      'Stap-voor-stap guides per CMS',
      'Koppel WordPress, Shopify, GBP, GA4',
      'Voortgang per dienst — meten wat werkt',
    ],
    cta: { label: 'Start Self-Service', href: '/register' },
    accent: '#2563eb',
    popular: false,
  },
  {
    id: 'managed',
    label: 'Managed AI Bot',
    price: '€995,95',
    period: 'excl. btw · eerste maand',
    secondary: 'Daarna €2.495,95 p/m excl. btw',
    tagline: 'AI doet het werk. U keurt strategisch goed.',
    bullets: [
      'AI Bot voert ECHT uit (geen nep-statussen)',
      'WordPress/Shopify auto-fixes via REST API',
      'Wix/Squarespace: kant-en-klare snippets',
      'Joomla/Drupal: ons ops-team plaatst (SLA 5 werkdagen)',
      'Google Ads, Meta, TikTok & Snapchat-beheer',
      '25-40 backlinks per maand, branche-specifiek',
      'AI content & blog publicatie',
      'Maandelijks rapport + strategiegesprek',
      'Prioriteit support < 4 uur',
    ],
    cta: { label: 'Bekijk Managed AI', href: '/prijzen#managed' },
    accent: '#D4A017',
    popular: true,
  },
];

const HONEST_PROOF = [
  { icon: Bot, title: 'AI voert ECHT uit', desc: 'Onze cms_executor pusht via REST API. Geen schermpje dat "uitgevoerd" liegt.' },
  { icon: Activity, title: 'Eerlijke statussen', desc: 'U ziet exact: queued · wacht op koppeling · ops-team plaatst · uitgevoerd met live-link.' },
  { icon: Link2, title: '80+ directories', desc: 'Branche-specifiek prioriteit. Restaurants krijgen Iens/TheFork, bouwers Werkspot/HomeDeal.' },
  { icon: ShieldCheck, title: 'Geen lock-in', desc: 'Maandelijks opzegbaar. Uw data blijft van u. Wij leveren waarde of u stopt.' },
];

const SERVICES = [
  {
    icon: Search,
    title: 'AI SEO',
    seoIntent: 'beter vindbaar in Google',
    desc: 'AI-audit van technische SEO + content + structured data. Voor WordPress/Shopify pushen wij de fixes direct via REST API.',
    bullets: ['H1 / meta / titles automatisch', 'Schema.org markup', 'Interne linkstructuur', 'Page-speed score'],
    href: '/diensten#seo',
  },
  {
    icon: Target,
    title: 'AI Google Ads (SEA)',
    seoIntent: 'advertenties slimmer laten draaien',
    desc: 'AI-gestuurde campagnes met dagelijkse optimalisatie op zoekintenties die converteren — niet op klikken.',
    bullets: ['Kwaliteits-score-monitoring', 'Negatieve zoekwoorden auto', 'Bid-strategie per zoekterm', 'Conversie-attributie'],
    href: '/diensten#sea',
  },
  {
    icon: Globe,
    title: 'Meta · TikTok · Snapchat Ads',
    seoIntent: 'social media advertenties beheren',
    desc: 'Doelgroep-targeting, creatieve A/B-tests en realtime budget-allocatie op platforms waar uw klanten zijn.',
    bullets: ['Look-a-like audiences', 'UGC-creatief templates', 'ROAS-tracking', 'Cross-channel rapport'],
    href: '/diensten#social-ads',
  },
  {
    icon: MapPin,
    title: 'Google Bedrijfsprofiel',
    seoIntent: 'lokale vindbaarheid verbeteren',
    desc: 'Volledig geoptimaliseerd profiel met wekelijkse posts, foto-management en review-respons door AI.',
    bullets: ['Categorieën + diensten', 'Wekelijkse Local Posts', 'Review-respons AI', 'Q&A-monitoring'],
    href: '/diensten#gbp',
  },
  {
    icon: Link2,
    title: 'Backlinks & Listings',
    seoIntent: 'autoriteit en backlinks bouwen',
    desc: '80+ NL directories in onze catalog. AI selecteert branche-specifieke en hoog-DA listings — geen massa-spam.',
    bullets: ['Algemene NL gidsen', 'Branche-directories', 'Reviewplatforms (Trustpilot/Kiyoh)', 'Maandelijks 25-40 nieuwe'],
    href: '/diensten#backlinks',
  },
  {
    icon: Wrench,
    title: 'Website Optimalisatie',
    seoIntent: 'website automatisch verbeteren',
    desc: 'Continue mutaties op uw bestaande site: speed, UX, conversie-elementen. Auto-push voor WP/Shopify; snippets voor Wix.',
    bullets: ['Core Web Vitals', 'Above-fold optimalisatie', 'CTA + formulier-tweaks', 'Mobile-first'],
    href: '/diensten#website-optimalisatie',
  },
  {
    icon: Crown,
    title: 'Website Redesign',
    seoIntent: 'website laten vernieuwen',
    desc: 'Strategisch redesign van uw bestaande site — conversie-eerst, niet design-first. In Managed AI inbegrepen.',
    bullets: ['User flow audit', 'Hero + structuur rewrite', 'CMS-migratie waar nodig', 'A/B-test launch'],
    href: '/diensten#redesign',
  },
  {
    icon: Sparkles,
    title: 'Nieuwe Website Laten Maken',
    seoIntent: 'nieuwe website laten maken',
    desc: 'Voor klanten zonder website: AI-gegenereerde basis + handmatige finetune door ons team. In Managed AI inbegrepen.',
    bullets: ['AI content + structuur', 'CMS keuze (WP/Shopify)', 'Hosting + domeinen', 'Live binnen 14 dagen'],
    href: '/diensten#nieuwe-website',
  },
  {
    icon: BarChart3,
    title: 'Google Analytics & Dashboard',
    seoIntent: 'meer leads uit mijn website',
    desc: 'GA4 + GSC + conversie-events. Custom dashboard waarin u per dienst en kanaal ziet wat omzet oplevert.',
    bullets: ['GA4 via Google Tag Manager', 'Conversie-doelen', 'Custom rapporten', 'Wekelijkse highlights'],
    href: '/diensten#analytics',
  },
  {
    icon: TrendingUp,
    title: 'Conversie-Optimalisatie',
    seoIntent: 'meer klanten uit mijn website',
    desc: 'AI-analyse van uitvallers, hitmap-data en formulier-completion. Iteratieve verbeteringen per maand.',
    bullets: ['CRO-audit per pagina', 'Heatmap + scroll-depth', 'Formulier-optimalisatie', 'CTA-positionering'],
    href: '/diensten#cro',
  },
  {
    icon: FileText,
    title: 'Content & Strategie',
    seoIntent: 'meer organisch verkeer',
    desc: 'AI schrijft, ons team redigeert. Maandelijks 4-8 SEO-geoptimaliseerde artikelen die uw expertise bewijzen.',
    bullets: ['Keyword research', 'Topic clusters', 'Blog + landingspages', 'AI-publicatie naar CMS'],
    href: '/diensten#content',
  },
  {
    icon: ShieldCheck,
    title: 'Lokale Online Zichtbaarheid',
    seoIntent: 'lokale klanten aantrekken',
    desc: 'Combinatie van GBP, lokale citations, branche-listings en geo-targeted ads. Voor zaken met een verzorgingsgebied.',
    bullets: ['Citation-consistentie', 'Lokale landingspages', 'Geo-Ads', 'Buurt-content'],
    href: '/diensten#lokaal',
  },
];

const HOW_STEPS = [
  { n: 1, icon: Search, title: 'Gratis online groeiscan', desc: 'Vul uw website-URL in. Binnen 60 seconden krijgt u uw score + top-10 verbeterpunten + 3 backlink-kansen.', cta: 'Direct scannen', href: '/register' },
  { n: 2, icon: Plug, title: 'Koppel uw kanalen (gratis)', desc: 'Koppel WordPress, Shopify, Google Bedrijfsprofiel, GA4 of GSC. We lezen alleen — schrijven pas nadat u "ja" zegt.', cta: 'Integraties', href: '/diensten#integraties' },
  { n: 3, icon: Bot, title: 'Kies: zelf uitvoeren of AI laten doen', desc: 'Connected Self-Service (€14,95 p/m): u doet zelf met AI-suggesties. Managed AI: onze bot voert daadwerkelijk uit.', cta: 'Bekijk prijzen', href: '/prijzen' },
  { n: 4, icon: Activity, title: 'Eerlijke uitvoering, meetbare groei', desc: 'In uw dashboard ziet u per opdracht: queued · in uitvoering · uitgevoerd met live-link. Geen nep-statussen.', cta: 'Hoe het werkt', href: '/werkwijze' },
];

const FAQ = [
  {
    q: 'Wat is het verschil tussen een marketingbureau en IMPERIA AI?',
    a: 'Een klassiek bureau factureert uren. Wij zijn een AI-first platform met menselijk vangnet: AI voert het standaardwerk uit (WordPress-fixes, snippets, backlinks-outreach, ads-optimalisatie) en onze specialisten handelen de uitzonderingen af. U ziet realtime wat er gebeurt — geen black box, geen vage rapportages.',
  },
  {
    q: 'Werkt dit ook als ik geen WordPress heb?',
    a: 'Ja. Wij ondersteunen 3 uitvoer-modi: (1) Auto voor WordPress, Shopify, Webflow, Magento — AI pusht direct via API. (2) Snippet voor Wix, Squarespace, Webador, JouwWeb — wij genereren kant-en-klare blokken die u in 30 seconden plakt. (3) Team voor Joomla, Drupal, Mijndomein — ons ops-team plaatst handmatig binnen 5 werkdagen.',
  },
  {
    q: 'Kan ik op elk moment opzeggen?',
    a: 'Ja. Connected Self-Service (€14,95 p/m) is maandelijks opzegbaar. Managed AI start met de launch-maand (€995,95) en gaat daarna over in een maandelijks contract van €2.495,95 — ook maandelijks opzegbaar. Geen jaarcontracten, geen lock-in.',
  },
  {
    q: 'Hoeveel backlinks krijg ik écht per maand?',
    a: 'Tussen 25 en 40 nieuwe backlinks per maand op Managed AI. We hebben 80+ Nederlandse directories in onze catalog, waarvan 46+ branche-specifiek. AI prioriteert op domain authority en uw branche — restaurants krijgen Iens/TheFork, bouwers krijgen Werkspot/HomeDeal/Bouwwereld.',
  },
  {
    q: 'Hoe voorkomt u dat AI iets fouts doet op mijn website?',
    a: 'Drie lagen veiligheid: (1) Alle mutaties hebben een approval-flag — u keurt strategisch goed, niet per fix. (2) Wij maken een backup voor élke wijziging. (3) U kunt elke actie terugdraaien in het dashboard. Daarnaast loggen wij élke wijziging met evidence-URL zodat u live kunt verifiëren.',
  },
  {
    q: 'Is er een minimumcontract voor de gratis scan?',
    a: 'Nee. De gratis scan is volledig vrijblijvend — vul uw website-URL in en u krijgt direct uw groeiscore + top-10 verbeterpunten + 3 backlink-kansen. Geen creditcard, geen verplichtingen.',
  },
];

/* ─── Schema.org structured data ─── */
const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "IMPERIA AI",
    "url": "https://www.imperiaai.nl",
    "logo": "https://www.imperiaai.nl/logo.png",
    "description": "AI Marketing Manager voor MKB & zzp in Nederland. Wij verbeteren uw online vindbaarheid, optimaliseren uw Google Bedrijfsprofiel, bouwen lokale SEO en maken websites voor zzp en mkb. Gratis groeiscan binnen 60 seconden.",
    "address": { "@type": "PostalAddress", "addressLocality": "Zwolle", "addressCountry": "NL" },
    "sameAs": [],
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "IMPERIA AI",
    "url": "https://www.imperiaai.nl",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://www.imperiaai.nl/scan?url={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "IMPERIA AI — AI Marketing Platform",
    "description": "AI-gestuurd marketing-platform met gratis groeiscan, Connected Self-Service (€14,95 p/m) en Managed AI Bot.",
    "brand": "IMPERIA AI",
    "offers": [
      { "@type": "Offer", "name": "Gratis Online Groeiscan", "price": "0", "priceCurrency": "EUR", "availability": "https://schema.org/InStock" },
      { "@type": "Offer", "name": "Connected Self-Service", "price": "14.95", "priceCurrency": "EUR", "availability": "https://schema.org/InStock" },
      { "@type": "Offer", "name": "Managed AI Bot (Launch)", "price": "995.95", "priceCurrency": "EUR", "availability": "https://schema.org/InStock" },
      { "@type": "Offer", "name": "Managed AI Bot (Recurring)", "price": "2495.95", "priceCurrency": "EUR", "availability": "https://schema.org/InStock" },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  },
];

export default function HomePage() {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');

  const startScan = (e) => {
    e?.preventDefault();
    const clean = (url || '').trim();
    if (!clean || clean.length < 3) return;
    let normalized = clean;
    if (!normalized.startsWith('http://') && !normalized.startsWith('https://')) normalized = 'https://' + normalized;
    navigate(`/register?url=${encodeURIComponent(normalized)}`);
  };

  return (
    <>
      <SEOHead
        title="AI Marketing Manager voor MKB · Online vindbaarheid verbeteren"
        description="Uw AI Marketing Manager voor MKB & zzp. Verbeter uw online vindbaarheid, optimaliseer uw Google Bedrijfsprofiel en laat uw website werken. Gratis groeiscan binnen 60 seconden. Connected vanaf €14,95 p/m, Managed AI Bot vanaf €995,95 excl. btw eerste maand."
        canonical="/"
        keywords="AI marketing manager mkb, online vindbaarheid verbeteren, google bedrijfsprofiel optimalisatie, website laten maken zzp, website laten maken mkb, lokale seo nederland, ai seo nederland, ai marketing platform, hoger in google mkb, meer klanten via google"
        schema={SCHEMA}
      />

      {/* ═══════════════════════════════ HERO ═══════════════════════════════ */}
      <section style={{
        background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 60%, #0f172a 100%)',
        padding: '120px 0 90px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: '10%', right: '5%', width: 480, height: 480, borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,160,23,0.18), transparent)', filter: 'blur(80px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-10%', left: '5%', width: 380, height: 380, borderRadius: '50%', background: 'radial-gradient(circle, rgba(79,70,229,0.12), transparent)', filter: 'blur(60px)', pointerEvents: 'none' }} />

        <div className="container-custom max-w-6xl mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 bg-[#D4A017]/15 border border-[#D4A017]/30 rounded-full px-3 py-1.5 mb-6">
                <Sparkles size={12} className="text-[#D4A017]" />
                <span className="text-[#D4A017] text-xs font-bold tracking-wide uppercase">AI Marketing Manager voor MKB & zzp · Nederland</span>
              </span>
              <h1
                data-testid="home-hero-title"
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] mb-6"
                style={{ fontFamily: "'Sora', sans-serif" }}>
                Uw <span style={{ color: '#D4A017' }}>AI Marketing Manager</span> — voor betere vindbaarheid, meer klanten, lagere kosten
              </h1>
              <p className="text-white/75 text-base md:text-lg leading-relaxed mb-7 max-w-xl">
                IMPERIA AI is uw AI Marketing Manager voor MKB & zzp. We verbeteren uw online vindbaarheid, optimaliseren uw Google Bedrijfsprofiel, bouwen aan lokale SEO en — indien nodig — laten we uw website helemaal opnieuw maken. Start met een gratis online groeiscan en zie binnen 60 seconden waar de groei vastzit.
              </p>

              {/* Inline scan form */}
              <form onSubmit={startScan} data-testid="hero-scan-form" className="mb-5">
                <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-2 flex flex-col sm:flex-row gap-2 max-w-xl">
                  <div className="flex-1 flex items-center gap-2 bg-white rounded-xl px-3.5 py-3">
                    <Globe size={16} className="text-slate-400 shrink-0" />
                    <input
                      data-testid="hero-scan-input"
                      type="text"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      placeholder="uwbedrijf.nl"
                      autoComplete="url"
                      className="flex-1 bg-transparent outline-none text-[#0f172a] font-medium text-sm placeholder:text-slate-400"
                    />
                  </div>
                  <button
                    data-testid="hero-scan-submit"
                    type="submit"
                    style={{ background: '#D4A017', color: '#0f172a', padding: '14px 22px', borderRadius: 12, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    Start gratis scan <ArrowRight size={15} />
                  </button>
                </div>
                <p className="text-white/55 text-xs mt-2 ml-2">Geen creditcard. Geen verplichtingen. Resultaat in 60 seconden.</p>
              </form>

              {/* Geen website CTA — alternatief voor klanten zonder site */}
              <div className="max-w-xl -mt-2 mb-5 flex items-center gap-3 flex-wrap">
                <span className="text-white/45 text-xs">Nog geen website?</span>
                <a
                  href="/website-laten-bouwen"
                  data-testid="hero-geen-website-cta"
                  className="text-[#D4A017] hover:text-[#e6b52a] text-xs font-semibold inline-flex items-center gap-1 transition-colors"
                >
                  Laat er één voor u bouwen <ArrowRight size={12} />
                </a>
              </div>

              {/* Quick proofs */}
              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-6">
                {['80+ NL directories', 'Auto-push naar WP & Shopify', '25-40 backlinks/maand', 'Maandelijks opzegbaar'].map((p) => (
                  <span key={p} className="inline-flex items-center gap-1.5 text-white/70 text-xs">
                    <CheckCircle2 size={12} className="text-[#D4A017]" /> {p}
                  </span>
                ))}
              </div>
            </div>

            {/* Right — visual mockup of dashboard */}
            <div className="hidden lg:block">
              <div style={{
                background: 'white', borderRadius: 18, padding: 18,
                boxShadow: '0 20px 60px -10px rgba(0,0,0,0.4)',
                transform: 'rotate(1.5deg)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <p style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Live uitvoering · Voorbeeld</p>
                  <span style={{ background: '#dcfce7', color: '#15803d', padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700 }}>● Live</span>
                </div>
                {[
                  { s: 'completed', t: 'H1 toegevoegd op homepage', m: 'WordPress · auto-push', c: '#15803d', bg: '#dcfce7' },
                  { s: 'queued', t: 'Schema.org markup toevoegen', m: 'Shopify · in wachtrij', c: '#a16207', bg: '#fef9c3' },
                  { s: 'awaiting', t: 'GA4 koppeling activeren', m: 'Wacht op uw goedkeuring', c: '#0369a1', bg: '#e0f2fe' },
                  { s: 'team', t: 'Local Business Schema (Joomla)', m: 'Ops-team plaatst · SLA 5d', c: '#0e7490', bg: '#cffafe' },
                ].map((row, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: row.bg, borderRadius: 10, marginBottom: 8 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: row.c }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{row.t}</p>
                      <p style={{ fontSize: 11, color: row.c, fontWeight: 600 }}>{row.m}</p>
                    </div>
                  </div>
                ))}
                <p style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', marginTop: 4, fontStyle: 'italic' }}>
                  Eerlijke statussen — exact wat AI doet en waarom.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════ TIERS ═══════════════════════════════ */}
      <section id="prijzen" style={{ background: '#f8fafc', padding: '80px 0 70px' }}>
        <div className="container-custom max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <p style={{ fontSize: 11, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8 }}>
              Eerlijke prijsstructuur
            </p>
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 36, fontWeight: 800, color: '#0f172a', lineHeight: 1.15, marginBottom: 10 }}>
              Drie manieren om te starten — kies wat past
            </h2>
            <p style={{ fontSize: 15, color: '#64748b', maxWidth: 640, margin: '0 auto', lineHeight: 1.55 }}>
              Probeer eerst gratis. Doe het daarna zelf met AI-suggesties voor €14,95 p/m, of laat onze AI Bot het volledig uitvoeren vanaf €995,95 excl. btw eerste maand.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {TIERS.map((t) => (
              <div key={t.id} data-testid={`tier-${t.id}`} style={{
                background: t.popular ? 'linear-gradient(180deg, #0f172a 0%, #1e1b4b 100%)' : 'white',
                color: t.popular ? 'white' : '#0f172a',
                borderRadius: 18, padding: '24px 22px',
                border: t.popular ? '2px solid #D4A017' : '1px solid #e2e8f0',
                position: 'relative',
                boxShadow: t.popular ? '0 20px 50px -10px rgba(212,160,23,0.3)' : '0 2px 8px rgba(0,0,0,0.04)',
              }}>
                {t.popular && (
                  <span style={{
                    position: 'absolute', top: -12, right: 20,
                    background: '#D4A017', color: '#0f172a', padding: '4px 12px',
                    borderRadius: 999, fontSize: 10, fontWeight: 800, letterSpacing: '0.06em',
                  }}>
                    POPULAIR
                  </span>
                )}
                <div style={{ marginBottom: 14 }}>
                  <p style={{ fontSize: 11, color: t.popular ? '#D4A017' : t.accent, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                    {t.label}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontFamily: "'Sora',sans-serif", fontSize: 32, fontWeight: 800 }}>{t.price}</span>
                    <span style={{ fontSize: 12, opacity: 0.7 }}>{t.period}</span>
                  </div>
                  {t.secondary && (
                    <p style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>{t.secondary}</p>
                  )}
                  <p style={{ fontSize: 13, marginTop: 8, lineHeight: 1.4, opacity: 0.9 }}>{t.tagline}</p>
                </div>
                <ul style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
                  {t.bullets.map((b, i) => (
                    <li key={i} style={{ display: 'flex', gap: 8, fontSize: 13, lineHeight: 1.5 }}>
                      <CheckCircle2 size={14} style={{ color: t.popular ? '#D4A017' : t.accent, flexShrink: 0, marginTop: 2 }} />
                      <span style={{ opacity: 0.92 }}>{b}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  data-testid={`tier-cta-${t.id}`}
                  to={t.cta.href}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    padding: '11px 18px', borderRadius: 10,
                    background: t.popular ? '#D4A017' : t.accent,
                    color: t.popular ? '#0f172a' : 'white',
                    fontSize: 13, fontWeight: 700, textDecoration: 'none',
                  }}>
                  {t.cta.label} <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
          <p style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8', marginTop: 18 }}>
            Alle prijzen exclusief btw. Maandelijks opzegbaar. Setup-kosten vervallen bij directe start.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════ HONEST PROOF ═══════════════════════════════ */}
      <section style={{ background: 'white', padding: '70px 0 60px' }}>
        <div className="container-custom max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <p style={{ fontSize: 11, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8 }}>
              Waarom IMPERIA AI anders is
            </p>
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 32, fontWeight: 800, color: '#0f172a', lineHeight: 1.15 }}>
              Geen marketingbureau-trucs. Geen valse &ldquo;uitgevoerd&rdquo;-badges.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {HONEST_PROOF.map((p) => (
              <div key={p.title} style={{
                background: '#f8fafc', borderRadius: 14, padding: '20px 18px',
                border: '1px solid #e2e8f0',
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: 'linear-gradient(135deg, #D4A017, #fbbf24)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12,
                }}>
                  <p.icon size={20} style={{ color: '#0f172a' }} />
                </div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 5 }}>
                  {p.title}
                </h3>
                <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.55 }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════ SERVICES (12) ═══════════════════════════════ */}
      <section id="diensten" style={{ background: '#0f172a', padding: '90px 0 80px', color: 'white' }}>
        <div className="container-custom max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <p style={{ fontSize: 11, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8 }}>
              Diensten — wat onze AI voor u uitvoert
            </p>
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 36, fontWeight: 800, color: 'white', lineHeight: 1.15, marginBottom: 10 }}>
              12 marketing-diensten · één AI bot · één dashboard
            </h2>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.7)', maxWidth: 640, margin: '0 auto', lineHeight: 1.55 }}>
              Van SEO tot Google Ads, van backlinks tot conversie-optimalisatie. Onze AI bot voert de meest tijdrovende taken zelf uit en ons team handelt de strategische beslissingen af.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICES.map((s) => (
              <Link key={s.title} to={s.href} data-testid={`service-${s.title.toLowerCase().replace(/\s+/g, '-')}`}
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: 14, padding: '20px 18px',
                  border: '1px solid rgba(255,255,255,0.08)',
                  textDecoration: 'none', color: 'white',
                  display: 'flex', flexDirection: 'column', gap: 8,
                  transition: 'all 0.2s',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(212,160,23,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <s.icon size={18} style={{ color: '#D4A017' }} />
                  </div>
                  <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 15, fontWeight: 800 }}>{s.title}</h3>
                </div>
                <p style={{ fontSize: 10, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                  Zoekintentie: {s.seoIntent}
                </p>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55 }}>{s.desc}</p>
                <ul style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {s.bullets.map((b, i) => (
                    <li key={i} style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', background: 'rgba(255,255,255,0.05)', padding: '2px 8px', borderRadius: 6 }}>
                      {b}
                    </li>
                  ))}
                </ul>
                <p style={{ fontSize: 12, color: '#D4A017', fontWeight: 700, marginTop: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  Meer weten <ChevronRight size={12} />
                </p>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/diensten" style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#D4A017', color: '#0f172a',
              padding: '12px 24px', borderRadius: 10,
              fontWeight: 700, fontSize: 14, textDecoration: 'none',
            }}>
              Alle diensten in detail <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════ HOW IT WORKS ═══════════════════════════════ */}
      <section id="hoe-het-werkt" style={{ background: 'white', padding: '90px 0 80px' }}>
        <div className="container-custom max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <p style={{ fontSize: 11, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8 }}>
              Hoe het werkt
            </p>
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 36, fontWeight: 800, color: '#0f172a', lineHeight: 1.15, marginBottom: 10 }}>
              Van scan naar groei in 4 stappen
            </h2>
            <p style={{ fontSize: 15, color: '#64748b', maxWidth: 580, margin: '0 auto', lineHeight: 1.55 }}>
              Geen 6 weken intake-trajecten. Geen Excel-rapportages die niemand leest. Real-time inzicht en uitvoering vanaf dag één.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {HOW_STEPS.map((s) => (
              <div key={s.n} style={{
                background: '#f8fafc', borderRadius: 14, padding: '22px 20px',
                border: '1px solid #e2e8f0', position: 'relative',
              }}>
                <span style={{
                  position: 'absolute', top: -12, left: 20,
                  background: '#D4A017', color: '#0f172a',
                  width: 28, height: 28, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 800, fontSize: 13, fontFamily: "'Sora',sans-serif",
                }}>{s.n}</span>
                <s.icon size={22} style={{ color: '#D4A017', marginBottom: 8, marginTop: 4 }} />
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 5 }}>
                  {s.title}
                </h3>
                <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.55, marginBottom: 10 }}>{s.desc}</p>
                <Link to={s.href} style={{
                  fontSize: 12, color: '#0f172a', fontWeight: 700, textDecoration: 'none',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}>
                  {s.cta} <ChevronRight size={12} />
                </Link>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/werkwijze" style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              border: '1px solid #0f172a', color: '#0f172a',
              padding: '11px 22px', borderRadius: 10,
              fontWeight: 700, fontSize: 13, textDecoration: 'none',
            }}>
              Bekijk de complete werkwijze <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════ FOR-WHO STRIP ═══════════════════════════════ */}
      <section style={{ background: '#f8fafc', padding: '60px 0 60px' }}>
        <div className="container-custom max-w-6xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 28, fontWeight: 800, color: '#0f172a', lineHeight: 1.15, marginBottom: 6 }}>
              Voor wie is IMPERIA AI?
            </h2>
            <p style={{ fontSize: 14, color: '#64748b', maxWidth: 620, margin: '0 auto' }}>
              Wij werken met Nederlandse MKB die echt willen groeien — niet met multinationals en niet met hobby-startups.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { icon: Wrench, title: 'Lokale dienstverleners', desc: 'Bouw, horeca, kappers, garages — verbeter uw Google Bedrijfsprofiel en lokale ranking automatisch.' },
              { icon: Globe, title: 'Webshops & retail', desc: 'Shopify of WooCommerce — verhoog conversie via SEO, Trustpilot/Kiyoh-listings en slimmere ads.' },
              { icon: Crown, title: 'Service-bedrijven', desc: 'Consultancy, IT, marketing — bouw autoriteit via content, backlinks en branche-directories.' },
            ].map((c) => (
              <div key={c.title} style={{
                background: 'white', borderRadius: 14, padding: '20px 18px',
                border: '1px solid #e2e8f0',
              }}>
                <c.icon size={22} style={{ color: '#D4A017', marginBottom: 10 }} />
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 5 }}>{c.title}</h3>
                <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.55 }}>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════ FAQ ═══════════════════════════════ */}
      <section style={{ background: 'white', padding: '80px 0 70px' }}>
        <div className="container-custom max-w-3xl mx-auto px-4">
          <div className="text-center mb-10">
            <p style={{ fontSize: 11, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, marginBottom: 8 }}>
              Veelgestelde vragen
            </p>
            <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 32, fontWeight: 800, color: '#0f172a', lineHeight: 1.15 }}>
              Antwoorden op de meest gestelde vragen
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {FAQ.map((f, i) => (
              <details key={i} data-testid={`faq-${i}`} style={{
                background: '#f8fafc', borderRadius: 12, padding: '14px 18px',
                border: '1px solid #e2e8f0',
              }}>
                <summary style={{
                  cursor: 'pointer', fontWeight: 700, color: '#0f172a',
                  fontSize: 14, fontFamily: "'Sora',sans-serif",
                  listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  {f.q}
                  <ChevronRight size={14} style={{ color: '#94a3b8' }} />
                </summary>
                <p style={{ marginTop: 10, fontSize: 13, color: '#475569', lineHeight: 1.65 }}>{f.a}</p>
              </details>
            ))}
          </div>
          <p style={{ textAlign: 'center', marginTop: 24, fontSize: 13, color: '#64748b' }}>
            Andere vraag? <Link to="/contact" style={{ color: '#D4A017', fontWeight: 700, textDecoration: 'none' }}>Stuur ons een bericht →</Link>
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════ FINAL CTA ═══════════════════════════════ */}
      <section style={{ background: 'linear-gradient(135deg, #0f172a, #1e1b4b)', padding: '80px 0', color: 'white' }}>
        <div className="container-custom max-w-3xl mx-auto px-4 text-center">
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: 36, fontWeight: 800, color: 'white', lineHeight: 1.15, marginBottom: 12 }}>
            Klaar om uw <span style={{ color: '#D4A017' }}>website</span> te laten groeien?
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.75)', marginBottom: 28, maxWidth: 520, margin: '0 auto 28px', lineHeight: 1.55 }}>
            Start met een gratis online groeiscan. Binnen 60 seconden ziet u uw groeiscore en de eerste 10 verbeterpunten. Géén verplichtingen.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/register" data-testid="final-cta-scan" style={{
              background: '#D4A017', color: '#0f172a', padding: '14px 28px',
              borderRadius: 12, fontWeight: 700, fontSize: 14, textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              Start gratis scan <ArrowRight size={14} />
            </Link>
            <Link to="/prijzen" data-testid="final-cta-prijzen" style={{
              border: '1px solid rgba(255,255,255,0.3)', color: 'white',
              padding: '14px 28px', borderRadius: 12, fontWeight: 700, fontSize: 14, textDecoration: 'none',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              Bekijk prijzen
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
